import { useGetList, useTranslate, type Identifier } from "ra-core";
import { Fragment, useState } from "react";
import { Separator } from "@/components/ui/separator";

import type { Email, EmailRecipient } from "../types";
import { ComposeEmailDialog, type EmailDraft } from "./ComposeEmailDialog";
import type { TemplateVariables } from "./emailTemplates";
import { EmailItem } from "./EmailItem";
import { SendEmailButton } from "./SendEmailButton";

const MAX_EMAILS = 100;

/**
 * « E-mails » tab of a record: its emails, newest first, with a button to
 * write a new one and a reply on each.
 */
export const RecordEmails = ({
  filter,
  draft,
  suggestions,
  variables,
  companyId,
}: {
  /** e.g. { "contact_ids@cs": "{12}" } */
  filter: Record<string, unknown>;
  /** Draft of a new email from this record. */
  draft: EmailDraft;
  suggestions?: EmailRecipient[];
  variables?: TemplateVariables;
  companyId?: Identifier;
}) => {
  const translate = useTranslate();
  const [reply, setReply] = useState<EmailDraft | null>(null);
  const { data: emails = [], isPending } = useGetList<Email>("emails", {
    pagination: { page: 1, perPage: MAX_EMAILS },
    sort: { field: "sent_at", order: "DESC" },
    filter,
  });

  return (
    <div className="flex flex-col gap-3 pt-2">
      <div className="flex justify-end">
        <SendEmailButton
          draft={draft}
          suggestions={suggestions}
          variables={variables}
          companyId={companyId}
        />
      </div>
      {isPending ? null : emails.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.mail.emails.empty")}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {emails.map((email, index) => (
            <Fragment key={email.id}>
              <EmailItem email={email} onReply={setReply} />
              {index < emails.length - 1 ? <Separator /> : null}
            </Fragment>
          ))}
        </div>
      )}
      <ComposeEmailDialog
        open={reply != null}
        onClose={() => setReply(null)}
        draft={reply ?? { links: draft.links }}
        variables={variables}
        companyId={companyId}
      />
    </div>
  );
};
